import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { func, oneOf, string, object, bool } from 'prop-types';
import Button from '@Components/Button';
import { GLOBAL } from '@Config/global';
import { FORM_DATA } from '@Config/form_data';
import Input from '@Components/Input';
import OptimizedCombobox from '@Components/OptimizedCombobox';
import './styles.scss';

const { MODAL } = GLOBAL;

const NewUserModal = ({
  icon = 'bi bi-exclamation-circle',
  status = 'success',
  onConfirm,
  onDismiss,
  title = MODAL.TITLE,
  producer = false,
  user
}) => {
  // Convert producer prop to boolean to handle both string and boolean inputs
  const isProducer = producer === true || producer === 'true';
  
  // Local state for form data
  const [formData, setFormData] = useState({
    nombre: '',
    apellido: '',
    email: '',
    cuit: '',
    telephone: '',
    compania: ''
  });

  // Local state for validation errors
  const [errors, setErrors] = useState({});

  // Validation functions
  const validateField = useCallback((name, value) => {
    const newErrors = { ...errors };
    
    switch (name) {
      case 'nombre':
        if (!value.trim()) {
          newErrors.nombre = 'El nombre es obligatorio';
        } else if (value.length < 2) {
          newErrors.nombre = 'El nombre debe tener al menos 2 caracteres';
        } else if (value.length > 100) {
          newErrors.nombre = 'El nombre no puede exceder 100 caracteres';
        } else {
          delete newErrors.nombre;
        }
        break;
        
      case 'apellido':
        if (!value.trim()) {
          newErrors.apellido = 'El apellido es obligatorio';
        } else if (value.length < 2) {
          newErrors.apellido = 'El apellido debe tener al menos 2 caracteres';
        } else if (value.length > 100) {
          newErrors.apellido = 'El apellido no puede exceder 100 caracteres';
        } else {
          delete newErrors.apellido;
        }
        break;
        
      case 'email':
        if (!value.trim()) {
          newErrors.email = 'El correo electrónico es obligatorio';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          newErrors.email = 'Ingrese un correo electrónico válido';
        } else if (value.length > 100) {
          newErrors.email = 'El correo electrónico no puede exceder 100 caracteres';
        } else {
          delete newErrors.email;
        }
        break;
        
      case 'cuit':
        if (isProducer && value.trim()) {
          // CUIT/CUIL validation: 2 digits - dash - 8-9 digits - dash - 1 digit
          const cuitRegex = /^\d{2}\d{8,9}\d{1}$/;
          if (!cuitRegex.test(value)) {
            newErrors.cuit = 'Formato de CUIT/CUIL inválido (ej: 20123456789)';
          } else {
            delete newErrors.cuit;
          }
        } else {
          delete newErrors.cuit;
        }
        break;
        
      case 'telephone':
        if (isProducer) {
          if (!value.trim()) {
            newErrors.telephone = 'El teléfono es obligatorio para productores';
          } else if (!/^\+?\d{7,15}$/.test(value.replace(/[-\s]/g, ''))) {
            newErrors.telephone = 'Ingrese un número de teléfono válido';
          } else {
            delete newErrors.telephone;
          }
        } else {
          delete newErrors.telephone;
        }
        break;
        
      case 'compania':
        if (isProducer && !value) {
          newErrors.compania = 'Debe seleccionar una compañía';
        } else {
          delete newErrors.compania;
        }
        break;
        
      default:
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [errors, isProducer]);

  // Validate entire form
  const validateForm = useCallback(() => {
    let isValid = true;
    Object.keys(formData).forEach(field => {
      if (!validateField(field, formData[field])) {
        isValid = false;
      }
    });
    return isValid;
  }, [formData, validateField]);

  // Sync local state with prop changes
  useEffect(() => {
    if (user) {
      setFormData({
        nombre: user.nombre || '',
        apellido: user.apellido || '',
        email: user.email || '',
        cuit: user.cuit || '',
        telephone: user.telephone || '',
        compania: user.compania || ''
      });
      // Clear errors when form is reset
      setErrors({});
    }
  }, [user]);

  const phoneCompanyOptions = useMemo(() => {
    return FORM_DATA.PHONE_COMPANIES.map(company => ({
      value: company.value,
      label: company.value
    }));
  }, []);

  // Handle input changes for Input
  const handleInputChange = useCallback((event) => {
    const inputName = event.target.name || event.target.id;
    const inputValue = event.target.value;
    
    // Update local state
    setFormData(prev => ({
      ...prev,
      [inputName]: inputValue
    }));
    
    // Validate field in real-time
    validateField(inputName, inputValue);
    
    // Update parent user object
    if (user && typeof user === 'object') {
      user[inputName] = inputValue;
    }
  }, [user, validateField]);

  // Handle select changes for OptimizedCombobox
  const handleComboboxChange = useCallback((option, syntheticEvent) => {
    const inputName = syntheticEvent.target.name;
    const inputValue = option.value;
    
    // Update local state
    setFormData(prev => ({
      ...prev,
      [inputName]: inputValue
    }));
    
    // Validate field in real-time
    validateField(inputName, inputValue);
    
    // Update parent user object
    if (user && typeof user === 'object') {
      user[inputName] = inputValue;
    }
  }, [user, validateField]);

  // Handle form submission
  const handleConfirm = useCallback(() => {
    // Validate entire form before submission
    if (!validateForm()) {
      return;
    }
    
    // Ensure the user object has all the latest data
    if (user && typeof user === 'object') {
      Object.keys(formData).forEach(key => {
        user[key] = formData[key];
      });
    }
    onConfirm(user);
  }, [user, formData, onConfirm, validateForm]);

  // Check if form is valid for submit button
  const isFormValid = useMemo(() => {
    const requiredFields = ['nombre', 'apellido', 'email'];
    if (isProducer) {
      requiredFields.push('telephone', 'compania');
    }
    
    // Check if all required fields have values
    const hasAllRequiredFields = requiredFields.every(field =>
      formData[field] && formData[field].trim() !== ''
    );
    
    // Check if there are no errors
    const hasNoErrors = Object.keys(errors).length === 0;
    
    return hasAllRequiredFields && hasNoErrors;
  }, [formData, errors, isProducer]);

  return (
    <div 
      className="modal modalNewUser fade show d-block" 
      tabIndex="-1" 
      role="dialog" 
      aria-hidden="false"
      aria-labelledby="modal-title"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
    >
      <div className="modal-dialog modal-dialog-centered" role="document">
        <div className="modal-content">
          <div className="modal-header border-0 justify-content-end">
            <button
              type="button"
              className="btn-close"
              onClick={onDismiss}
              aria-label="Close"
            />
          </div>

          {status === 'warning' && (
            <div className="text-center mb-3">
              <i className={`${icon}`} style={{ fontSize: '3rem', color: '#ffc107' }}></i>
            </div>
          )}
          <div className="modal-body p-4 mb-4">
            <h5 id="modal-title" className="modal-title text-center mb-4">{title}</h5>
            {/* Name and Surname Row */}
            <div className='row mb-3'>
              <div className='col'>
                <Input
                  id="nombre"
                  label="Nombre *"
                  type="text"
                  value={formData.nombre}
                  onChange={handleInputChange}
                  maxLength="100"
                  placeholder="Ingrese el nombre"
                  className={errors.nombre ? 'is-invalid' : ''}
                />
                {errors.nombre && (
                  <div className="invalid-feedback d-block">
                    {errors.nombre}
                  </div>
                )}
              </div>
              <div className='col'>
                <Input
                  id="apellido"
                  label="Apellido *"
                  type="text"
                  value={formData.apellido}
                  onChange={handleInputChange}
                  maxLength="100"
                  placeholder="Ingrese el apellido"
                  className={errors.apellido ? 'is-invalid' : ''}
                />
                {errors.apellido && (
                  <div className="invalid-feedback d-block">
                    {errors.apellido}
                  </div>
                )}
              </div>
            </div>

            {/* CUIT Row (Producer Only) */}
            {isProducer && (
              <div className='row mb-3'>
                <div className='col-6'>
                  <Input
                    id="cuit"
                    label="CUIT/CUIL"
                    type="text"
                    value={formData.cuit}
                    onChange={handleInputChange}
                    maxLength="13"
                    placeholder="ej: 20123456789"
                    className={errors.cuit ? 'is-invalid' : ''}
                  />
                  {errors.cuit && (
                    <div className="invalid-feedback d-block">
                      {errors.cuit}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Email Row */}
            <div className='row mb-3'>
              <div className='col'>
                <Input
                  id="email"
                  label="Correo electrónico *"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  maxLength="100"
                  placeholder="ej: usuario@ejemplo.com"
                  className={errors.email ? 'is-invalid' : ''}
                />
                {errors.email && (
                  <div className="invalid-feedback d-block">
                    {errors.email}
                  </div>
                )}
              </div>
            </div>

            {/* Phone and Company Row (Producer Only) */}
            {isProducer && (
              <div className='row mb-3'>
                <div className='col-6'>
                  <Input
                    id="telephone"
                    label="Teléfono *"
                    type="tel"
                    value={formData.telephone}
                    onChange={handleInputChange}
                    maxLength="100"
                    placeholder="ej: 1112345678"
                    className={errors.telephone ? 'is-invalid' : ''}
                  />
                  {errors.telephone && (
                    <div className="invalid-feedback d-block">
                      {errors.telephone}
                    </div>
                  )}
                </div>
                <div className='col-6'>
                  <OptimizedCombobox
                    id="compania"
                    name="compania"
                    label="Compañía *"
                    options={phoneCompanyOptions}
                    value={formData.compania}
                    onChange={handleComboboxChange}
                    placeholder="Seleccionar compañía"
                    isSearchable={true}
                    useFixedPosition={true}
                    className={errors.compania ? 'has-error' : ''}
                  />
                  {errors.compania && (
                    <div className="invalid-feedback d-block">
                      {errors.compania}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          
          <div className="modal-actions m-0 p-3 d-flex justify-content-end gap-2">
            <Button 
              customStyles={{ minWidth: '120px' }} 
              type="button" 
              color="secondary" 
              fill="outline" 
              size="smaller-outline" 
              data-dismiss="modalNewUser" 
              onClick={onDismiss}
              aria-label="Cancelar"
            >
              Cancelar
            </Button>
            <Button
              customStyles={{ minWidth: '120px', border: 'none' }}
              type="button"
              size="smaller"
              color={status}
              onClick={handleConfirm}
              aria-label="Guardar usuario"
              disabled={!isFormValid}
            >
              Guardar
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

NewUserModal.propTypes = {
  onConfirm: func.isRequired,
  onDismiss: func.isRequired,
  title: string,
  status: oneOf(['primary', 'warning', 'error', 'success']),
  icon: string,
  producer: bool,
  user: object.isRequired
};

export default React.memo(NewUserModal);
